// Knowledge Base — upload PDF/DOCX/TXT/MD docs the AI bot answers from.
import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, Trash2, Download, BookOpen, Database } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

export default function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const fileRef = useRef(null);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/kb/documents");
      setDocs(r.data.documents || []);
      setStatus(r.data.kb_status || null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function doUpload(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "");
    fd.append("tags", tags || "");
    try {
      const r = await api.post("/admin/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added "${r.data.title}" — ${r.data.char_count} chars indexed`);
      setTitle(""); setTags("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this document from the knowledge base?")) return;
    try { await api.delete(`/admin/kb/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  function download(id) {
    const token = localStorage.getItem("c7_token");
    window.open(`${API}/admin/kb/documents/${id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="kb-title">
            <BookOpen className="w-6 h-6 text-primary" /> Knowledge Base
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload documents the AI bot answers from. Supported: PDF, DOCX, TXT, MD.
          </p>
        </div>
        {status && (
          <div className="text-right text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2" data-testid="kb-status">
            <div className="flex items-center gap-1.5 justify-end font-medium text-foreground"><Database className="w-3.5 h-3.5" /> {status.docs_count} docs</div>
            <div>{status.chunks_count} chunks · vocab {status.vocab_size}</div>
          </div>
        )}
      </div>

      {canWrite && (
        <Card className="p-5 mb-6 space-y-4" data-testid="kb-upload-card">
          <div className="flex items-center gap-2"><Upload className="w-4 h-4 text-primary" /><h2 className="font-display font-bold">Upload a document</h2></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Title (optional)</Label><Input data-testid="kb-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Refund Policy" /></div>
            <div><Label>Tags (comma-separated)</Label><Input data-testid="kb-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="billing, refunds" /></div>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" data-testid="kb-file-input"
              onChange={(e) => doUpload(e.target.files?.[0])} />
            <Button data-testid="kb-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Choose file & upload"}
            </Button>
            <span className="text-xs text-muted-foreground">Text is extracted for the bot; the original stays downloadable.</span>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : docs.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="kb-empty">No documents yet. Upload one above to power the AI bot.</div>
        ) : (
          <table className="w-full text-sm" data-testid="kb-doc-table">
            <thead className="bg-muted/40 text-xs text-muted-foreground uppercase tracking-wide">
              <tr><th className="text-left px-4 py-2.5">Document</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Size</th><th className="text-left px-4 py-2.5">Chars</th><th className="px-4 py-2.5"></th></tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const dt = d.data || {};
                return (
                  <tr key={d.id} className="border-t hover:bg-muted/20" data-testid={`kb-doc-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium"><FileText className="w-4 h-4 text-muted-foreground" /> {dt.title || d.name}</div>
                      {(dt.tags || []).length > 0 && <div className="mt-1 flex gap-1 flex-wrap">{dt.tags.map((t) => <span key={t} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{t}</span>)}</div>}
                    </td>
                    <td className="px-4 py-3 uppercase text-xs text-muted-foreground">{dt.file_ext || "txt"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtBytes(dt.size)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{dt.char_count || 0}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`kb-download-${d.id}`} onClick={() => download(d.id)}><Download className="w-4 h-4" /></Button>
                      {canDelete && <Button size="sm" variant="ghost" className="text-destructive" data-testid={`kb-delete-${d.id}`} onClick={() => del(d.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
